import type { Capabilities, SessionInfo } from './types';

/**
 * The server surface, now small on purpose.
 *
 * Page operations happen in the browser, so the only things that still need the
 * server are who you are, what it can convert, and Office conversion itself.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** A request that never reached the server, as opposed to one it refused. */
export class OfflineError extends Error {
  constructor() {
    super('The server could not be reached.');
  }
}

async function parseError(response: Response): Promise<never> {
  let message = `Request failed (${response.status}).`;
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') message = body.error;
  } catch {
    // Non-JSON error body; keep the generic message.
  }
  throw new ApiError(response.status, message);
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(path, { credentials: 'same-origin', ...init });
  } catch {
    // fetch only rejects on a network-level failure, which is what tells us
    // we are offline rather than unauthorised.
    throw new OfflineError();
  }
}

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) await parseError(response);
  return response.json() as Promise<T>;
}

export async function fetchMe(): Promise<SessionInfo> {
  return json(await request('/api/me'));
}

export async function fetchCapabilities(): Promise<Capabilities> {
  return json(await request('/api/capabilities'));
}

/** Sends one file to LibreOffice on the server and returns the PDF it produced. */
export async function convertToPdf(file: File): Promise<Uint8Array> {
  const form = new FormData();
  form.append('file', file);

  const response = await request('/api/convert', { method: 'POST', body: form });
  if (!response.ok) await parseError(response);
  return new Uint8Array(await response.arrayBuffer());
}

export async function signOut(): Promise<void> {
  const response = await request('/auth/logout', { method: 'POST' });
  const body = await json<{ redirectTo: string }>(response);
  window.location.href = body.redirectTo;
}

/** Hands the finished bytes to the browser as a download. */
export function downloadBlob(bytes: Uint8Array, filename: string, mimeType: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
